import React, { useState, useMemo } from 'react';
import { Link } from 'wouter';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { SecurityUtils } from '../../utils/security';
import { Input } from '../DesignSystem/primitives/Input';
import './RegisterPage.css';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
    <path d="M1 1h22v22H1z" fill="none" />
  </svg>
);

interface RegisterPageProps {
  embedded?: boolean;
}

// Validation des contraintes de mot de passe (Supabase config: 9 chars, maj, min, special)
const validatePassword = (pwd: string) => {
  return {
    minLength: pwd.length >= 9,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
  };
};

const getPasswordStrength = (pwd: string): { level: 'weak' | 'medium' | 'strong'; percent: number } => {
  if (!pwd) return { level: 'weak', percent: 0 };
  const checks = validatePassword(pwd);
  const passed = Object.values(checks).filter(Boolean).length;
  if (passed <= 1) return { level: 'weak', percent: 25 };
  if (passed <= 2) return { level: 'medium', percent: 50 };
  if (passed <= 3) return { level: 'medium', percent: 75 };
  return { level: 'strong', percent: 100 };
};

const RegisterPage: React.FC<RegisterPageProps> = ({ embedded = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string }>({});
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean; confirmPassword?: boolean }>({});

  const { register, signInWithGoogle } = useAuth();
  const { navigate } = useWouterRouter();

  // Validation en temps réel
  const passwordChecks = useMemo(() => validatePassword(password), [password]);
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (Supabase: 9 chars, maj, min, special)
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const checks = validatePassword(password);
      if (!checks.minLength) {
        newErrors.password = 'Password must be at least 9 characters';
      } else if (!checks.hasUppercase) {
        newErrors.password = 'Password must contain an uppercase letter';
      } else if (!checks.hasLowercase) {
        newErrors.password = 'Password must contain a lowercase letter';
      } else if (!checks.hasSpecial) {
        newErrors.password = 'Password must contain a special character';
      }
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marquer tous les champs comme touchés
    setTouched({ email: true, password: true, confirmPassword: true });

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const sanitizedEmail = SecurityUtils.sanitizeInputForSubmission(email);
      await register(sanitizedEmail, password);
      toast.success(
        'Registration successful! Please check your email to confirm your account.',
        { duration: 5000 }
      );
      navigate('/verify-email');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration error';

      // Display error inline based on type
      if (errorMessage.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors(prev => ({ ...prev, password: errorMessage }));
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      toast.success('Sign up successful!', { duration: 3000 });
      navigate('/organizer');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration error';
      
      if (errorMessage.includes('MetaMask') || errorMessage.includes('Crypto extension')) {
        toast.error(
          'Crypto extension detected.\nPlease try email/password login or use "Forgot Password" with your Google email.',
          { duration: 10000 }
        );
      } else {
        toast.error(errorMessage, { duration: 10000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`register-container ${embedded ? 'register-container--embedded' : ''}`}>
      <div className="register-card">
        {!embedded && (
          <button onClick={handleBack} className="back-button">
            ← Back
          </button>
        )}
        <h2>Register</h2>
        <form onSubmit={handleSubmit} className="register-form">
          <Input
            id="email"
            name="email"
            type="email"
            label="Email"
            value={email}
            onChange={(val) => {
              setEmail(val);
              if (touched.email) {
                setErrors(prev => ({ ...prev, email: undefined }));
              }
            }}
            error={touched.email ? errors.email : undefined}
            required
          />

          <Input
            id="password"
            name="password"
            type="password"
            label="Password"
            value={password}
            onChange={(val) => {
              setPassword(val);
              if (touched.password) {
                setErrors(prev => ({ ...prev, password: undefined }));
              }
            }}
            error={touched.password ? errors.password : undefined}
            required
          />

          {/* Password strength indicator */}
          {password && (
            <div className="password-strength">
              <div className="password-strength__bar">
                <div
                  className={`password-strength__fill password-strength__fill--${passwordStrength.level}`}
                  style={{ width: `${passwordStrength.percent}%` }}
                />
              </div>
              <div className="password-requirements">
                <span className={`requirement ${passwordChecks.minLength ? 'requirement--valid' : ''}`}>
                  {passwordChecks.minLength ? '✓' : '○'} At least 9 characters
                </span>
                <span className={`requirement ${passwordChecks.hasUppercase ? 'requirement--valid' : ''}`}>
                  {passwordChecks.hasUppercase ? '✓' : '○'} One uppercase letter
                </span>
                <span className={`requirement ${passwordChecks.hasLowercase ? 'requirement--valid' : ''}`}>
                  {passwordChecks.hasLowercase ? '✓' : '○'} One lowercase letter
                </span>
                <span className={`requirement ${passwordChecks.hasSpecial ? 'requirement--valid' : ''}`}>
                  {passwordChecks.hasSpecial ? '✓' : '○'} One special character (!@#$%...)
                </span>
              </div>
            </div>
          )}

          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(val) => {
              setConfirmPassword(val);
              if (touched.confirmPassword) {
                setErrors(prev => ({ ...prev, confirmPassword: undefined }));
              }
            }}
            error={touched.confirmPassword ? errors.confirmPassword : undefined}
            required
          />

          {/* Confirmation match indicator */}
          {confirmPassword && password && (
            <div className={`password-match ${password === confirmPassword ? 'password-match--valid' : 'password-match--invalid'}`}>
              {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
            </div>
          )}
          <button type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
          <div className="separator">or</div>

          <button 
            type="button"
            onClick={handleGoogleSignIn}
            className="google-button"
            disabled={loading}
          >
            <GoogleIcon />
            Sign up with Google
          </button>

          <div className="auth-links">
            <p>Already have an account?</p>
            <Link to="/login" className="login-link">
              Sign in
            </Link>
          </div>
        </form>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

export default RegisterPage;